import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  dateFnsLocalizer,
  type Event as RbcEvent,
  type EventProps as RbcEventProps,
  type SlotInfo,
} from 'react-big-calendar';
import withDragAndDrop, {
  type EventInteractionArgs,
} from 'react-big-calendar/lib/addons/dragAndDrop';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { pl } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import type { AppointmentItem, UserDirectoryItem } from '@crm/shared';
import { useAppointmentUpdate, extractWarnings } from './api';
import { extractApiError } from '../../lib/api';

const locales = { 'pl-PL': pl };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales,
});

type CalendarEvent = RbcEvent & {
  appointment: AppointmentItem;
  resourceId: number;
};

export type CalendarSlot = {
  startAt: Date;
  employeeId: number | null;
};

type Props = {
  view: 'day' | 'week';
  date: Date;
  employees: UserDirectoryItem[];
  appointments: AppointmentItem[];
  onSlotClick: (slot: CalendarSlot) => void;
  onEventClick: (item: AppointmentItem) => void;
};

const DEFAULT_COLOR = '#ec4899'; // brand-500

const DnDCalendar = withDragAndDrop<CalendarEvent>(Calendar);

function statusLabelKey(status: AppointmentItem['status']): string {
  switch (status) {
    case 'BOOKED':
      return 'appointments.status.BOOKED';
    case 'CONFIRMED':
      return 'appointments.status.CONFIRMED';
    case 'COMPLETED':
      return 'appointments.status.COMPLETED';
    case 'CANCELLED':
      return 'appointments.status.CANCELLED';
    case 'NO_SHOW':
      return 'appointments.status.NO_SHOW';
  }
}

export function AppointmentsCalendar({
  view,
  date,
  employees,
  appointments,
  onSlotClick,
  onEventClick,
}: Props): ReactNode {
  const { t } = useTranslation();
  // Tylko jedna mutacja na raz per drag (rbc nie konstruuje wielokrotnych
  // równoległych dropów). Reuse'em useAppointmentUpdate musielibyśmy znać id
  // z góry — robimy to lokalnie przez axios w handlerze.
  const [dragMutationFor, setDragMutationFor] = useState<number | null>(null);
  const updateForId = useAppointmentUpdate(dragMutationFor ?? 0);

  const events: CalendarEvent[] = useMemo(
    () =>
      appointments.map((a) => ({
        title: `${a.client.firstName}${a.client.lastName ? ` ${a.client.lastName}` : ''}`,
        start: new Date(a.startAt),
        end: new Date(a.endAt),
        resourceId: a.employeeId,
        appointment: a,
      })),
    [appointments],
  );

  const resources = useMemo(
    () =>
      employees.map((e) => ({
        id: e.id,
        title: `${e.firstName} ${e.lastName}`,
        color: e.color ?? DEFAULT_COLOR,
      })),
    [employees],
  );

  function handleSelectSlot(slot: SlotInfo): void {
    const resourceId = (slot.resourceId ?? null) as number | null;
    onSlotClick({ startAt: slot.start, employeeId: resourceId });
  }

  function handleSelectEvent(event: CalendarEvent): void {
    onEventClick(event.appointment);
  }

  async function handleEventDrop(args: EventInteractionArgs<CalendarEvent>): Promise<void> {
    const { event, start, resourceId } = args;
    const apt = event.appointment;

    // Drag między pracownikami — niedozwolony (decyzja Fazy 7).
    if (resourceId !== undefined && resourceId !== null && resourceId !== apt.employeeId) {
      window.alert(t('appointments.dnd.crossResourceBlocked'));
      return;
    }

    // Wizyty terminalne (CANCELLED/COMPLETED/NO_SHOW) — niedostępne do edycji.
    if (
      apt.status === 'CANCELLED' ||
      apt.status === 'COMPLETED' ||
      apt.status === 'NO_SHOW'
    ) {
      window.alert(t('appointments.dnd.terminalBlocked'));
      return;
    }

    setDragMutationFor(apt.id);
    const startISO = (start instanceof Date ? start : new Date(start)).toISOString();

    try {
      await updateForId.mutateAsync({ startAt: startISO });
    } catch (err) {
      const warnings = extractWarnings(err);
      if (warnings.length > 0) {
        const lines = warnings.map((w) => `• ${w.message}`).join('\n');
        const ok = window.confirm(`${t('appointments.warnings.confirmTitle')}\n\n${lines}`);
        if (ok) {
          try {
            await updateForId.mutateAsync({ startAt: startISO, force: true });
          } catch (forceErr) {
            window.alert(extractApiError(forceErr));
          }
        }
      } else {
        window.alert(extractApiError(err));
      }
    } finally {
      setDragMutationFor(null);
    }
  }

  function eventPropGetter(event: CalendarEvent): { style: React.CSSProperties; className: string } {
    const emp = employees.find((e) => e.id === event.resourceId);
    const baseColor = emp?.color ?? DEFAULT_COLOR;
    const muted =
      event.appointment.status === 'CANCELLED' || event.appointment.status === 'NO_SHOW';

    return {
      style: {
        backgroundColor: muted ? '#a1a1aa' : baseColor,
        borderColor: muted ? '#71717a' : baseColor,
        color: '#fff',
        opacity: muted ? 0.55 : 0.95,
        textDecoration: event.appointment.status === 'CANCELLED' ? 'line-through' : undefined,
      },
      className: '',
    };
  }

  // Granice godzin pokazywanych w kalendarzu — salon zazwyczaj 08:00–22:00.
  const minTime = useMemo(() => {
    const d = new Date();
    d.setHours(8, 0, 0, 0);
    return d;
  }, []);
  const maxTime = useMemo(() => {
    const d = new Date();
    d.setHours(22, 0, 0, 0);
    return d;
  }, []);

  // Render eventu — kompaktowa wersja z godziną + statusem dla widoczności.
  // Faza 18: ikona 📌 + tooltip z fragmentem głównej notatki klienta
  // (jeśli klient ją ma i viewer ją widzi).
  const EventComponent = ({ event }: RbcEventProps<CalendarEvent>): ReactNode => {
    const a = event.appointment;
    const start = new Date(a.startAt);
    const time = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
    const mainNote = a.client.mainNote;
    const noteTooltip = mainNote
      ? `${t('clientNote.bannerTitle')}: ${mainNote.bodyPreview}${mainNote.hasMore ? '…' : ''}`
      : undefined;
    return (
      <div className="text-xs leading-tight">
        <div className="font-medium">
          {mainNote && (
            <span
              aria-label={t('clientNote.iconLabel') ?? 'main note'}
              title={noteTooltip}
              className="mr-1"
            >
              📌
            </span>
          )}
          {event.title}
        </div>
        <div>
          {time} · {a.totalDuration}m · {a.totalPrice} zł
        </div>
        {a.status !== 'BOOKED' && (
          <div className="text-[10px] uppercase opacity-90">{t(statusLabelKey(a.status))}</div>
        )}
      </div>
    );
  };

  return (
    <div className="appointments-calendar" style={{ height: 700 }}>
      <DnDCalendar
        localizer={localizer}
        culture="pl-PL"
        events={events}
        startAccessor="start"
        endAccessor="end"
        view={view}
        onView={() => undefined /* sterowane przez parenta */}
        date={date}
        onNavigate={() => undefined /* sterowane przez parenta */}
        views={['day', 'week']}
        toolbar={false}
        step={15}
        timeslots={4}
        min={minTime}
        max={maxTime}
        selectable
        resizable={false}
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        onEventDrop={handleEventDrop}
        draggableAccessor={(event: CalendarEvent) =>
          event.appointment.status === 'BOOKED' || event.appointment.status === 'CONFIRMED'
        }
        eventPropGetter={eventPropGetter}
        components={{ event: EventComponent }}
        resources={view === 'day' ? resources : undefined}
        resourceIdAccessor={(resource: object) => (resource as { id: number }).id}
        resourceTitleAccessor={(resource: object) => (resource as { title: string }).title}
        resourceAccessor={(event: object) => (event as { resourceId: number }).resourceId}
        messages={{
          today: t('appointments.today'),
          previous: '‹',
          next: '›',
          day: t('appointments.view.day'),
          week: t('appointments.view.week'),
          noEventsInRange: t('appointments.empty'),
          allDay: '',
          date: 'Data',
          time: 'Godzina',
          event: 'Wizyta',
        }}
      />
    </div>
  );
}
